import Cookies from "js-cookie";
import { Analytics, EcommerceEvent } from "./types";

type GoogleBookEnvironment = "production" | "sandbox";

export class GoogleBookSourceProvider {
  initialize(client: GoogleBookClient) {
    let urlParams = new URLSearchParams(window.location.search);

    const rwgToken = urlParams.get("rwg_token");

    if (rwgToken) {
      Cookies.set("rwg_token", rwgToken, { expires: 30 });
    }

    return GoogleBookAnalytics(client.environment);
  }
}

interface GoogleBookClientOptions {
  sendPageView?: boolean;
}

export interface GoogleBookClient extends GoogleBookClientOptions {
  provider: "google_book";
  environment: GoogleBookEnvironment;
}

export function makeGoogleBookClient(
  environment: GoogleBookEnvironment,
  options?: GoogleBookClientOptions
): GoogleBookClient {
  return {
    provider: "google_book",
    environment,
    sendPageView: options?.sendPageView ?? true,
  };
}

function GoogleBookAnalytics(environment: GoogleBookEnvironment): Analytics {
  return {
    group: () => {},
    register: () => {},
    identify: () => {},
    screen: () => {},
    // This provider exists solely to report booking conversions back to Google's
    // Actions Center. Every other ecommerce event belongs to the other providers,
    // so ignoring them is the expected outcome, not a failure.
    ecommerceEvent: (event: EcommerceEvent) => {
      if (event.event !== "Order Completed") {
        return;
      }

      reportConversion(environment, event.merchantId);
    },
    event: () => {},
  };
}

function reportConversion(
  environment: GoogleBookEnvironment,
  conversionMerchantId?: string
) {
  const rwgToken =
    // since it is sandbox environment, we are using a hardcoded token as advised by their documentation
    // this is also to ensure we can start tracking the conversion events for sandbox environment so that we don't have to create the orders manually on production
    environment === "sandbox"
      ? "AJKvS9WeONmWKEwjG0--HdpzMq0yAVNL8KMxbb44QtbcxMhSx_NUud5b8PLUBFehAIxOBO-iYRIJOknEFkIJmdsofdVJ6uOweQ=="
      : Cookies.get("rwg_token");

  if (!rwgToken || !conversionMerchantId) {
    return;
  }

  const savedMerchantId = Cookies.get("merchant_id");

  // 2 = the customer completed the transaction through the original merchant.
  // 1 = the customer left the original merchant's website and completed the
  // purchase through our platform with a different merchant.
  const merchantChanged = savedMerchantId === conversionMerchantId ? 2 : 1;

  const url =
    environment === "production"
      ? "https://www.google.com/maps/conversion/collect"
      : "https://www.google.com/maps/conversion/debug/collect";

  // Sent no-cors, so the response is opaque: it can never be parsed, and a
  // rejection only means the request never left the browser. Neither outcome is
  // actionable for whoever is looking at the console, so both stay quiet.
  fetch(url, {
    method: "POST",
    mode: "no-cors",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      // This merchant_id must be the same as the one you have shared with Google in your feed.
      // On Google Actions Center, under Configuration > Account and Users
      conversion_partner_id: 20002921,
      rwg_token: rwgToken,
      merchant_changed: merchantChanged,
    }),
  }).catch(() => {});
}
